import { useEffect, useRef } from 'react';
import { firstRunForksFromState, visibleSetupModalItems, type SetupModalItem } from '@ruta/shared';
import { fireSetupModalEvent } from '@/lib/postImportFirstRun';
import { useOrganization } from './useQueries';
import { useUserInfo } from './useUserInfo';

/**
 * Setup-modal events after import (RUT-3501). Payments / Team items are not
 * fired on a path the three forks hid. Overlay copy is RUT-3503.
 */
export function usePostImportSetupModalAnalytics(active: boolean): {
  trackItemClicked: (item: SetupModalItem) => void;
  trackDismissed: (item: SetupModalItem) => void;
  trackCompleted: (item: SetupModalItem) => void;
} {
  const user = useUserInfo();
  const { data } = useOrganization({ enabled: active });
  const forks = firstRunForksFromState(data?.organization?.firstRun);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!active || shownRef.current || !user.orgId || !forks) return;
    shownRef.current = true;
    for (const item of visibleSetupModalItems(forks)) {
      fireSetupModalEvent('shown', {
        orgId: user.orgId,
        role: user.role,
        item,
        forks,
      });
    }
  }, [active, forks, user.orgId, user.role]);

  const track = (kind: 'item_clicked' | 'dismissed' | 'completed') => (item: SetupModalItem) => {
    if (!user.orgId || !forks) return;
    fireSetupModalEvent(kind, { orgId: user.orgId, role: user.role, item, forks });
  };

  return {
    trackItemClicked: track('item_clicked'),
    trackDismissed: track('dismissed'),
    trackCompleted: track('completed'),
  };
}
